import React, {ChangeEvent, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {countrySpecificDistanceUnit} from '@utils/misc/functions';
import VehicleDetails from '@components/panels/VehicleDetails';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import SubmitBtn from '@components/buttons/FormButtons/SubmitBtn';
import Label from '@components/inputs/text/Label';
import NumericInput from '@components/inputs/NumericInput';
import {textBoxStyle} from '@utils/styles/textboxStyle';
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";
import {NavBar} from '@components/navigation/NavBar/NavBar';
import DropDownMenu from '@components/inputs/DropDownMenu';
import useServiceHook from '@hooks/useServiceHook';
import {VehicleService} from '@services/Vehicles/Vehicles';
import {IFleetioStatus} from '@views/Cancellations/CancellationVehicleDetails';
import {CurrentVehicleAction, CurrentVehicleState} from './currentVehicleDetailsReducer';
import {SelectedSubscriptionState} from "@views/VehicleExchange/selectedSubscriptionReducer";
import {Country} from '@utils/constants/localisation';
import {clearVehicleLocationChoice} from '@utils/constants/hubs';

import {ExchangeReasonState, ExchangeTypes} from './exchangeReasonReducer';
import {
  DAMS_CONTRACTED_AWAITING_REPAIR_STATUS,
  DAMS_CROSS_HIRE_ACTIVE_STATUS,
  DEPOT_AVAILABLE_STATUS
} from '@utils/constants/fleetioStatuses';


interface ICurrentVehicleProps {
  navigateTarget: string;
  selectedSubscriptionState: SelectedSubscriptionState;
  currentVehicleState: CurrentVehicleState;
  currentVehicleDispatcher: React.Dispatch<CurrentVehicleAction>;
  exchangeReasonState: ExchangeReasonState;
}

export default function CurrentVehicle({
  navigateTarget,
  selectedSubscriptionState,
  currentVehicleState,
  currentVehicleDispatcher,
  exchangeReasonState,
}: ICurrentVehicleProps) {
  const {custId, country} = useParams() as { custId: string; country: string };
  const navigate = useNavigate();


  // We get the vehicle ID from selected Subscription. Prefer the temp vehicle ID if present
  const currVehicleID = selectedSubscriptionState.temporaryVehicleId ? selectedSubscriptionState.temporaryVehicleId : selectedSubscriptionState.permanentVehicleId;
  const [loading, error, errMessage, currentVehicleDetails] = useServiceHook(
      VehicleService.getVehicleById,
      [currVehicleID, country],
      true,
      [currVehicleID],
  );

  useEffect(() => {
    // When we get a vehicle, update the state via dispatcher
    if (currentVehicleDetails) {
      currentVehicleDispatcher({
        type: 'SET_VEHICLE',
        payload: currentVehicleDetails,
      });
    }
  }, [currentVehicleDetails]);
  const vehicle = currentVehicleState?.vehicle;
  console.log('Current vehicle details:', vehicle);

  const onEndingOdometerChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    currentVehicleDispatcher({
      type: 'SET_ENDING_ODOMETER',  // Dispatch action to update the endingOdometer
      payload: value,
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(navigateTarget);
  };



  // A DAMS Cross Hire has its vehicle statuses chosen for the hub user. On the way out the
  // exchange type says so. On the way back the customer is on an ordinary Permanent exchange, so
  // the cross hire vehicle's own Fleetio status is what identifies it.
  // A DAMS Cross Hire from a cross hire vehicle is a swap, which works like a temporary to
  // temporary exchange: the vehicle handed back is not the contracted one, so nothing is
  // pre-selected for it.
  const isOnDamsCrossHire = vehicle?.vehicle_status_name === DAMS_CROSS_HIRE_ACTIVE_STATUS;
  const isDamsOutbound = exchangeReasonState.exchangeType === ExchangeTypes.DAMS_Cross_Hire
      && !isOnDamsCrossHire;
  const isDamsReturn = exchangeReasonState.exchangeType === ExchangeTypes.Permanent
      && isOnDamsCrossHire;

  // Which status to pre-select. Null means keep the existing behaviour - the first in the list.
  // On the way out the contracted vehicle is off hire but not yet booked in anywhere, so it goes
  // to the awaiting-repair status; the hub moves it on to smash repair by hand when it goes in.
  const damsStatusToPreselect = isDamsOutbound
      ? DAMS_CONTRACTED_AWAITING_REPAIR_STATUS
      : isDamsReturn ? DEPOT_AVAILABLE_STATUS : null;

  // The DAMS statuses are only offered through the dams filter, so they never show up on an
  // ordinary exchange. The filter also returns the normal exchange statuses, so the CSR can still
  // pick something else - a cross hire vehicle returned damaged belongs in a repair status.
  const statusFilter = damsStatusToPreselect ? 'dams' : 'exchange';

  const [statusLoading, , , statuses] = useServiceHook(
    VehicleService.getVehicleStatuses,
    [statusFilter, country],
    true,
    // The returning-vehicle status arrives with the vehicle, so the filter can change once it
    // loads. Refetch when it does.
    [statusFilter]
  );

  const isGB = country === Country.GB;
  const [locationsLoading, , , vehicleLocations] = useServiceHook<string[]>(
    VehicleService.getVehicleLocations,
    [country],
    isGB,
    []
  );

  const onSelectReturnLocation = (event: ChangeEvent<HTMLSelectElement>) => {
    currentVehicleDispatcher({
      type: 'SET_RETURN_LOCATION',
      payload: event.currentTarget.value,
    });
  };

  const onSelectStatus = (event: ChangeEvent<HTMLSelectElement>) => {
    const statusId = event.currentTarget.value;
    const statusName = event.target.options[event.target.selectedIndex].label;

    currentVehicleDispatcher({
      type: 'SET_FLEETIO_STATUS',
      payload: { id: statusId, name: statusName },
    });
  };

  useEffect(() => {
    // Set fleetio status of curr vehicle, when status dropdown changes
     if (!statusLoading && statuses && statuses.length > 0 && currentVehicleState.fleetioStatusName == "") {
      // Pre-select the DAMS status where there is one to pre-select, otherwise the first in the
      // list as before. If the DAMS name matches nothing Fleetio returned, fall back rather than
      // guess - the CSR then picks it themselves, which is the pre-DAMS behaviour.
      const damsStatus = damsStatusToPreselect
        ? statuses.find((item: IFleetioStatus) => item.name === damsStatusToPreselect)
        : undefined;
      const defaultStatus = damsStatus ?? statuses[0];
      currentVehicleDispatcher({
        type: 'SET_FLEETIO_STATUS',
        payload: {
          id: defaultStatus.id,
          name: defaultStatus.name,
        },
      });
    }
  }, [statusLoading, statuses]);

  useEffect(() => {
    // Set ending odometer to current meter value of vehicle when we get the vehicle details
      currentVehicleDispatcher({
        type: 'SET_ENDING_ODOMETER',
        payload: vehicle.current_meter_value,
      });
  }, [vehicle]);

    // Submit button is disabled if vehicle ID is not present, or (GB) no return location
    // (or "Blank") has been chosen yet — there is no plan exemption.
    const isSubmitDisabled = !(vehicle?.id && vehicle.id.trim().length > 0)
      || (isGB && !currentVehicleState.returnLocation);

  return (
    <>
      <NavBar pageTitle="Current vehicle details" backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={handleFormSubmit}>
        <p>Please confirm that vehicle details are correct</p>

        {vehicle ? (
          <>
            <VehicleDetails
              make={vehicle.make}
              model={vehicle.model}
              year={vehicle.year}
              color={vehicle.color}
              licensePlate={vehicle.license_plate}
              vin={vehicle.vin}
              bodyType={vehicle.body_type}
              startingMiles={vehicle.current_meter_value}
            />

            <Section
              childCmp={
                <>
                  <Label text={`Ending ${countrySpecificDistanceUnit(country)}`} requiredFieldInd={true} />
                  <NumericInput
                    style={textBoxStyle}
                    name="endingOdometer"
                    dataTestId="endingOdometer"
                    value={currentVehicleState.endingOdometer}
                    min={currentVehicleState.endingOdometer}
                    step="any"
                    required={true}
                    onChange={onEndingOdometerChange}
                  />
                  <p>Please double check the odometer value in the vehicle as part of the Exchange process.</p>
                </>
              }
            />
          </>
        ) : (
          <LoadingSpinner />
        )}


        {statuses ? (
          <Section
            childCmp={
              <>
                <Label text="Fleetio status for returning vehicle" requiredFieldInd={true} />
                {damsStatusToPreselect && (
                  <p style={{ margin: '0 0 1vh 0' }}>
                    Selected automatically for a DAMS Cross Hire. Change it if the vehicle is not
                    going back to the depot in this state.
                  </p>
                )}
                <DropDownMenu
                  menuName="fleetioStatus"
                  required={true}
                  // Controlled, so the dropdown always shows the status the journey holds. The
                  // DAMS pre-selection lands after the first render, which a default value ignores -
                  // the dropdown would show the first status while the pre-selected one was submitted.
                  value={currentVehicleState.fleetioStatusId}
                  defaultVal={{ value: currentVehicleState.fleetioStatusId, label: currentVehicleState.fleetioStatusName }}
                  choices={statuses.map((item: IFleetioStatus) => ({
                    label: item.name,
                    value: item.id,
                    enabled: true,
                  }))}
                  onSelect={onSelectStatus}
                  testId="fleetioStatus"
                />
              </>
            }
          />
        ) : (
          <LoadingSpinner />
        )}

        {isGB && (locationsLoading === false || locationsLoading === null ? (
          <Section
            childCmp={
              <>
                <Label text="Vehicle return location" requiredFieldInd={true} />
                <DropDownMenu
                  menuName="vehicleReturnLocation"
                  required={true}
                  // No defaultVal until one is chosen, so DropDownMenu renders a blank first
                  // option and the CSR has to make a deliberate choice.
                  defaultVal={currentVehicleState.returnLocation
                    ? { value: currentVehicleState.returnLocation, label: currentVehicleState.returnLocation }
                    : undefined}
                  choices={[
                    ...(vehicleLocations || []).map((location: string) => ({
                      label: location,
                      value: location,
                      enabled: true,
                    })),
                    clearVehicleLocationChoice,
                  ]}
                  onSelect={onSelectReturnLocation}
                  testId="vehicleReturnLocation"
                />
                <p>Where the returning vehicle is being left. This is recorded in Fleetio so the vehicle can be found at the right site.</p>
              </>
            }
          />
        ) : (
          <LoadingSpinner />
        ))}

        <SubmitBtn text="Next" breadcrumbObject={breadcrumbObject} disabled={isSubmitDisabled} />
      </form>
    </>
  );
}

const Section = ({ childCmp }: { childCmp: JSX.Element }) => (
  <div style={{ margin: '2.5vh 0vw' }}>{childCmp}</div>
);

const LoadingSpinner = () => (
  <div style={{ height: '5vh' }}>
    <Elliptical />
  </div>
);
